export type Language = "en" | "hi" | "gu";

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navbar
    navFeatures: "Features",
    navHowItWorks: "How It Works",
    navCommunity: "Community",
    navAdminPortal: "Community Admin Portal",
    navMemberPortal: "Member Portal",

    // Hero
    heroBadge: "#1 Community Management Platform",
    heroTitle1: "The Smartest Way to",
    heroTitle2: "Manage Your Community",
    heroTitle3: "",
    heroSubtitle:
      "An all-in-one digital platform to manage members, events, donations, facilities, and communications — built for modern community organizations.",
    heroBullet1: "Setup in under 5 minutes",
    heroBullet2: "24+ integrated modules",
    heroBullet3: "Trusted by 50+ communities",
    heroGetStarted: "Request a Demo",
    heroWatchDemo: "Watch Demo",
    statMembers: "Active Members",
    statCommunities: "Communities",
    statEvents: "Events Hosted",
    heroNetwork: "Network",
    heroCollaborate: "Collaborate",
    heroAchieve: "Achieve",
    heroVerified: "Verified",
    heroGovPartner: "Enterprise Ready",
    heroThisMonth: "This month",

    // Trust strip
    trustTitle: "Trusted by Leading Communities",
    trustStat1Val: "50+",
    trustStat1Label: "Communities Onboarded",
    trustStat2Val: "10,000+",
    trustStat2Label: "Members Managed",
    trustStat3Val: "₹2Cr+",
    trustStat3Label: "Donations Processed",
    trustStat4Val: "99.9%",
    trustStat4Label: "Uptime Guaranteed",

    // Features
    featuresTag: "Powerful Modules",
    featuresTitle1: "Everything You Need to",
    featuresTitle2: "Run Your Community",
    featuresSubtitle:
      "From member management to facility booking, from event coordination to financial reporting — one platform that does it all.",
    feat1Title: "Community Members",
    feat1Desc: "Manage families, individuals, and groups with rich profiles and smart search.",
    feat2Title: "Events & Programs",
    feat2Desc: "Create, promote, and manage events with RSVP, ticketing, and attendance tracking.",
    feat3Title: "Volunteering",
    feat3Desc: "Recruit, assign, and track volunteer contributions across all community activities.",
    feat4Title: "Donations & Payments",
    feat4Desc: "Accept donations, track pledges, issue receipts, and generate financial reports.",
    feat5Title: "Dharmashala Booking",
    feat5Desc: "Manage room inventory, online bookings, check-in/out, and occupancy reports.",
    feat6Title: "Temple Directory",
    feat6Desc: "Maintain a comprehensive directory of temples with timings, events, and contact info.",
    feat7Title: "Chat & Messaging",
    feat7Desc: "Real-time group chats, broadcast messages, and topic-based community channels.",
    feat8Title: "Gallery & Media",
    feat8Desc: "Photo albums, video galleries, and event highlights with easy sharing.",
    featCat1: "Member & Community",
    featCat2: "Events & Engagement",
    featCat3: "Facilities & Services",
    featCat4: "Communication & Analytics",
    feat_directory: "Member Directory",
    feat_membership: "Membership Cards",
    feat_birthday: "Birthday & Anniversary",
    feat_sadharmik: "Sadharmik Data",
    feat_refer: "Refer & Earn",
    feat_sponsorship: "Sponsorships",
    feat_auction: "Online Auction",
    feat_gallery: "Gallery & Media",
    feat_bhojanshala: "Bhojanshala Management",
    feat_hostel: "Hostel Management",
    feat_asset: "Asset Management",
    feat_stock: "Stock & Inventory",
    feat_announcements: "Feed & Announcements",
    feat_notifications: "Push Notifications",
    feat_whatsapp: "WhatsApp Integration",
    feat_reports: "Reports & Analytics",
    feat_feedback: "Feedback & Surveys",
    feat_total: "24+ Integrated Modules",
    feat_total_desc: "One platform to run your entire community",

    // How It Works
    howTag: "How It Works",
    howTitle1: "Go Live in",
    howTitle2: "4 Simple Steps",
    howSubtitle: "From signup to fully operational — get your community platform running in minutes, not months.",
    how1Title: "Sign Up & Configure",
    how1Desc: "Create your organization account, customize branding, and configure modules based on your community's needs.",
    how2Title: "Import Members",
    how2Desc: "Bulk upload your member database via Excel or add members individually. Instant onboarding with QR membership cards.",
    how3Title: "Launch & Engage",
    how3Desc: "Go live with events, announcements, facility bookings, and donation campaigns. Members get instant access via web or mobile.",
    how4Title: "Track & Grow",
    how4Desc: "Monitor engagement with real-time dashboards, financial reports, and analytics. Scale as your community grows.",

    // Community
    communityTag: "Who It's For",
    communityTitle1: "Built for Every",
    communityTitle2: "Stakeholder",
    communitySubtitle: "Whether you're managing operations, participating as a member, or supporting as a sponsor — we've got you covered.",
    comm1Title: "For Members",
    comm1Role: "Stay Connected & Engaged",
    comm1Desc: "Access your community directory, register for events, book facilities, donate, volunteer, and stay updated — all from one app.",
    comm1F1: "Digital membership card with QR",
    comm1F2: "Event registration & reminders",
    comm1F3: "Dharmashala & Bhojanshala booking",
    comm1F4: "Real-time chat & announcements",
    comm1Btn: "Explore Member Portal",
    comm2Title: "For Admins",
    comm2Role: "Complete Operational Control",
    comm2Desc: "A powerful admin dashboard to manage every aspect of your community — members, finances, events, facilities, and communications.",
    comm2F1: "360° admin dashboard with analytics",
    comm2F2: "Member & family management",
    comm2F3: "Donation tracking & financial reports",
    comm2F4: "WhatsApp & push notifications",
    comm2Btn: "Explore Admin Portal",
    comm3Title: "For Sponsors & Donors",
    comm3Role: "Maximize Your Impact",
    comm3Desc: "Transparent donation tracking, event sponsorship packages, and real-time impact reports. Every contribution is acknowledged.",
    comm3F1: "Custom sponsorship tiers",
    comm3F2: "Tax receipt generation",
    comm3F3: "Real-time donation dashboards",
    comm3F4: "Public recognition & badges",
    comm3Btn: "Explore Sponsorship",

    // CTA
    ctaTitle: "Ready to Transform Your Community?",
    ctaSubtitle: "Join 50+ communities already using our platform. Schedule a demo or start your free trial today.",
    ctaAdmin: "Admin Portal",
    ctaAdminDesc: "Full operational control",
    ctaMember: "Member Portal",
    ctaMemberDesc: "Engage & participate",
    ctaGetStarted: "Schedule a Demo",

    // Footer
    footerRights: "© 2026 Community Management Platform. All rights reserved.",
    footerProduct: "Product",
    footerCompany: "Company",
    footerLegal: "Legal",
    footerAbout: "About Us",
    footerCareers: "Careers",
    footerContact: "Contact",
    footerPrivacy: "Privacy Policy",
    footerTerms: "Terms of Service",
  },
  hi: {
    navFeatures: "सुविधाएं",
    navHowItWorks: "कैसे काम करता है",
    navCommunity: "समुदाय",
    navAdminPortal: "सामुदायिक एडमिन पोर्टल",
    navMemberPortal: "सदस्य पोर्टल",
    heroBadge: "#1 सामुदायिक प्रबंधन प्लेटफार्म",
    heroTitle1: "मजबूत बनाएं",
    heroTitle2: "समुदाय",
    heroTitle3: "एक साथ",
    heroSubtitle: "निर्बाध डिजिटल सहयोग, जुड़ाव और विकास के माध्यम से समुदायों को सशक्त बनाना। हजारों लोग अपने समुदायों को बदल रहे हैं।",
    heroBullet1: "शुरू करना आसान",
    heroBullet2: "सदस्यों के लिए मुफ्त",
    heroBullet3: "समुदायों द्वारा विश्वसनीय",
    heroGetStarted: "मुफ्त शुरू करें",
    heroWatchDemo: "डेमो देखें",
    statMembers: "सक्रिय सदस्य",
    statCommunities: "समुदाय",
    statEvents: "आयोजित कार्यक्रम",
    heroNetwork: "नेटवर्क",
    heroCollaborate: "सहयोग",
    heroAchieve: "उपलब्धि",
    heroVerified: "सत्यापित",
    heroGovPartner: "सामुदायिक भागीदार",
    heroThisMonth: "इस महीने",
    featuresTag: "सुविधाएं",
    featuresTitle1: "समुदाय बनाने के लिए",
    featuresTitle2: "सब कुछ आपके पास",
    featuresSubtitle: "हर स्तर पर कनेक्शन, सहयोग और सामुदायिक जुड़ाव को बढ़ावा देने के लिए शक्तिशाली उपकरण।",
    feat1Title: "सदस्य निर्देशिका",
    feat1Desc: "अपने नेटवर्क में सहकर्मियों, गुरुओं और सामुदायिक नेताओं से जुड़ें।",
    feat2Title: "कार्यक्रम और सभाएं",
    feat2Desc: "नेटवर्किंग इवेंट, कार्यशालाएं और सामुदायिक सभाएं खोजें।",
    feat3Title: "स्वयंसेवा",
    feat3Desc: "सार्थक स्वयंसेवी अवसर खोजें और अपने कौशल का योगदान दें।",
    feat4Title: "दान और प्रायोजन",
    feat4Desc: "निर्बाध दान और प्रायोजन उपकरणों से सामुदायिक पहल का समर्थन करें।",
    feat5Title: "धर्मशाला और भोजनशाला",
    feat5Desc: "सामुदायिक सुविधाएं बुक करें, कमरे प्रबंधित करें और भोजन सेवाओं तक पहुंचें।",
    feat6Title: "मंदिर निर्देशिका",
    feat6Desc: "सामुदायिक नेटवर्क में मंदिरों को खोजें और जुड़ें।",
    feat7Title: "चैट और मैसेजिंग",
    feat7Desc: "विषय-आधारित चर्चाओं और सहयोगी कार्यस्थलों में शामिल हों।",
    feat8Title: "गैलरी और मीडिया",
    feat8Desc: "सामुदायिक फोटो, वीडियो और इवेंट हाइलाइट्स साझा करें और ब्राउज़ करें।",
    featCat1: "सदस्य और समुदाय",
    featCat2: "कार्यक्रम और जुड़ाव",
    featCat3: "सुविधाएं और सेवाएं",
    featCat4: "संचार और विश्लेषण",
    feat_directory: "सदस्य निर्देशिका",
    feat_membership: "सदस्यता प्रबंधन",
    feat_birthday: "जन्मदिन और वर्षगांठ",
    feat_sadharmik: "साधार्मिक डेटा",
    feat_refer: "रेफर और कमाएं",
    feat_sponsorship: "प्रायोजन",
    feat_auction: "ऑनलाइन नीलामी",
    feat_gallery: "गैलरी और मीडिया",
    feat_bhojanshala: "भोजनशाला प्रबंधन",
    feat_hostel: "छात्रावास प्रबंधन",
    feat_asset: "संपत्ति प्रबंधन",
    feat_stock: "स्टॉक प्रबंधन",
    feat_announcements: "फीड और घोषणाएं",
    feat_notifications: "सूचनाएं",
    feat_whatsapp: "WhatsApp एकीकरण",
    feat_reports: "रिपोर्ट और विश्लेषण",
    feat_feedback: "प्रतिक्रिया प्रबंधन",
    feat_total: "24+ शक्तिशाली मॉड्यूल",
    feat_total_desc: "आपके समुदाय को प्रबंधित करने के लिए सब कुछ",
    howTag: "कैसे काम करता है",
    howTitle1: "आपकी यात्रा",
    howTitle2: "सामुदायिक जुड़ाव की ओर",
    howSubtitle: "मिनटों में शुरू करें और अपने समुदाय में प्रभाव डालना शुरू करें",
    how1Title: "अपनी प्रोफ़ाइल बनाएं",
    how1Desc: "साइन अप करें और अपनी सामुदायिक प्रोफ़ाइल बनाएं।",
    how2Title: "अवसर खोजें",
    how2Desc: "अपनी रुचियों के अनुसार कार्यक्रम, स्वयंसेवी अवसर और कनेक्शन ब्राउज़ करें।",
    how3Title: "जुड़ें और सहयोग करें",
    how3Desc: "समान विचारधारा के सामुदायिक सदस्यों से जुड़ें और चर्चाओं में शामिल हों।",
    how4Title: "प्रभाव डालें",
    how4Desc: "अपने योगदानों को ट्रैक करें और देखें कि आप क्या बदलाव ला रहे हैं।",
    communityTag: "समुदाय",
    communityTitle1: "अपना स्थान खोजें",
    communityTitle2: "समुदाय में",
    communitySubtitle: "चाहे आप एक सामुदायिक एडमिन हों, सक्रिय सदस्य हों, या स्वयंसेवक, आपके लिए यहां जगह है।",
    comm1Title: "सामुदायिक सदस्य",
    comm1Role: "सक्रिय सदस्य और परिवार",
    comm1Desc: "समर्पित सामुदायिक सदस्यों के नेटवर्क में शामिल हों।",
    comm1F1: "सदस्य निर्देशिका एक्सेस",
    comm1F2: "सामुदायिक कार्यक्रम",
    comm1F3: "सुविधाएं बुक करें",
    comm1F4: "स्वयंसेवी अवसर",
    comm1Btn: "सदस्यों को एक्सप्लोर करें",
    comm2Title: "सामुदायिक एडमिन",
    comm2Role: "प्रबंधन और नेतृत्व",
    comm2Desc: "एक केंद्रीकृत डैशबोर्ड से सामुदायिक संचालन की देखरेख करें।",
    comm2F1: "पूर्ण एडमिन डैशबोर्ड",
    comm2F2: "सदस्य प्रबंधन",
    comm2F3: "वित्तीय रिपोर्ट",
    comm2F4: "संचार उपकरण",
    comm2Btn: "एडमिन एक्सप्लोर करें",
    comm3Title: "स्वयंसेवक और प्रायोजक",
    comm3Role: "समर्थन और योगदान",
    comm3Desc: "स्वयंसेवा, प्रायोजन और दान के माध्यम से सामुदायिक पहल का समर्थन करें।",
    comm3F1: "प्रायोजन पैकेज",
    comm3F2: "इवेंट पार्टनरशिप",
    comm3F3: "दान ट्रैकिंग",
    comm3F4: "मान्यता और पुरस्कार",
    comm3Btn: "प्रायोजक एक्सप्लोर करें",
    ctaTitle: "बदलाव लाने के लिए तैयार हैं?",
    ctaSubtitle: "मजबूत समुदाय बनाने वाले हजारों सदस्यों से जुड़ें। अपना रास्ता चुनें और आज ही शुरू करें।",
    ctaAdmin: "एडमिन पोर्टल",
    ctaAdminDesc: "अपना समुदाय प्रबंधित करें",
    ctaMember: "सदस्य पोर्टल",
    ctaMemberDesc: "सदस्य के रूप में शामिल हों",
    ctaGetStarted: "अभी शुरू करें",
    footerRights: "© 2026 सामुदायिक प्रबंधन प्लेटफार्म। सर्वाधिकार सुरक्षित।",
    footerProduct: "उत्पाद",
    footerCompany: "कंपनी",
    footerLegal: "कानूनी",
    footerAbout: "हमारे बारे में",
    footerCareers: "करियर",
    footerContact: "संपर्क",
    footerPrivacy: "गोपनीयता नीति",
    footerTerms: "सेवा की शर्तें",
  },
  gu: {
    navFeatures: "સુવિધાઓ",
    navHowItWorks: "કેવી રીતે કામ કરે છે",
    navCommunity: "સમુદાય",
    navAdminPortal: "સામુદાયિક એડમિન પોર્ટલ",
    navMemberPortal: "સભ્ય પોર્ટલ",
    heroBadge: "#1 સામુદાયિક વ્યવસ્થાપન પ્લેટફોર્મ",
    heroTitle1: "મજબૂત બનાવો",
    heroTitle2: "સમુદાય",
    heroTitle3: "સાથે મળીને",
    heroSubtitle: "સીમલેસ ડિજિટલ સહયોગ, જોડાણ અને વિકાસ દ્વારા સમુદાયોને સશક્ત બનાવવા. હજારો લોકો તેમના સમુદાયોને બદલી રહ્યા છે.",
    heroBullet1: "શરૂ કરવું સરળ",
    heroBullet2: "સભ્યો માટે મફત",
    heroBullet3: "સમુદાયો દ્વારા વિશ્વસનીય",
    heroGetStarted: "મફત શરૂ કરો",
    heroWatchDemo: "ડેમો જુઓ",
    statMembers: "સક્રિય સભ્યો",
    statCommunities: "સમુદાયો",
    statEvents: "આયોજિત કાર્યક્રમો",
    heroNetwork: "નેટવર્ક",
    heroCollaborate: "સહયોગ",
    heroAchieve: "ઉપલબ્ધિ",
    heroVerified: "ચકાસાયેલ",
    heroGovPartner: "સામુદાયિક ભાગીદાર",
    heroThisMonth: "આ મહિને",
    featuresTag: "સુવિધાઓ",
    featuresTitle1: "સમુદાય બનાવવા માટે",
    featuresTitle2: "બધું તમારી પાસે",
    featuresSubtitle: "દરેક સ્તરે જોડાણ, સહયોગ અને સામુદાયિક જોડાણને પ્રોત્સાહન આપવા માટે શક્તિશાળી સાધનો.",
    feat1Title: "સભ્ય ડિરેક્ટરી",
    feat1Desc: "તમારા નેટવર્કમાં સાથીદારો, માર્ગદર્શકો અને સામુદાયિક નેતાઓ સાથે જોડાઓ.",
    feat2Title: "કાર્યક્રમો અને સભાઓ",
    feat2Desc: "નેટવર્કિંગ ઇવેન્ટ્સ, વર્કશોપ અને સામુદાયિક સભાઓ શોધો.",
    feat3Title: "સ્વયંસેવા",
    feat3Desc: "સાર્થક સ્વયંસેવી તકો શોધો અને તમારી કુશળતાનું યોગદાન આપો.",
    feat4Title: "દાન અને પ્રાયોજન",
    feat4Desc: "સીમલેસ દાન અને પ્રાયોજન સાધનોથી સામુદાયિક પહેલનું સમર્થન કરો.",
    feat5Title: "ધર્મશાળા અને ભોજનશાળા",
    feat5Desc: "સામુદાયિક સુવિધાઓ બુક કરો, રૂમ મેનેજ કરો અને ભોજન સેવાઓ ઍક્સેસ કરો.",
    feat6Title: "મંદિર ડિરેક્ટરી",
    feat6Desc: "સામુદાયિક નેટવર્કમાં મંદિરો શોધો અને જોડાઓ.",
    feat7Title: "ચેટ અને મેસેજિંગ",
    feat7Desc: "વિષય-આધારિત ચર્ચાઓ અને સહયોગી વર્કસ્પેસમાં જોડાઓ.",
    feat8Title: "ગેલેરી અને મીડિયા",
    feat8Desc: "સામુદાયિક ફોટો, વીડિયો અને ઇવેન્ટ હાઇલાઇટ્સ શેર કરો અને બ્રાઉઝ કરો.",
    featCat1: "સભ્ય અને સમુદાય",
    featCat2: "કાર્યક્રમો અને જોડાણ",
    featCat3: "સુવિધાઓ અને સેવાઓ",
    featCat4: "સંચાર અને વિશ્લેષણ",
    feat_directory: "સભ્ય ડિરેક્ટરી",
    feat_membership: "સભ્યતા વ્યવસ્થાપન",
    feat_birthday: "જન્મદિવસ અને વર્ષગાંઠ",
    feat_sadharmik: "સાધાર્મિક ડેટા",
    feat_refer: "રેફર અને કમાઓ",
    feat_sponsorship: "પ્રાયોજન",
    feat_auction: "ઓનલાઇન હરાજી",
    feat_gallery: "ગેલેરી અને મીડિયા",
    feat_bhojanshala: "ભોજનશાળા વ્યવસ્થાપન",
    feat_hostel: "છાત્રાલય વ્યવસ્થાપન",
    feat_asset: "સંપત્તિ વ્યવસ્થાપન",
    feat_stock: "સ્ટોક વ્યવસ્થાપન",
    feat_announcements: "ફીડ અને ઘોષણાઓ",
    feat_notifications: "સૂચનાઓ",
    feat_whatsapp: "WhatsApp એકીકરણ",
    feat_reports: "અહેવાલો અને વિશ્લેષણ",
    feat_feedback: "પ્રતિસાદ વ્યવસ્થાપન",
    feat_total: "24+ શક્તિશાળી મોડ્યુલો",
    feat_total_desc: "તમારા સમુદાયને મેનેજ કરવા માટે બધું",
    howTag: "કેવી રીતે કામ કરે છે",
    howTitle1: "તમારી યાત્રા",
    howTitle2: "સામુદાયિક જોડાણ તરફ",
    howSubtitle: "મિનિટોમાં શરૂ કરો અને તમારા સમુદાયમાં પ્રભાવ પાડવાનું શરૂ કરો",
    how1Title: "તમારી પ્રોફાઇલ બનાવો",
    how1Desc: "સાઇન અપ કરો અને તમારી સામુદાયિક પ્રોફાઇલ બનાવો.",
    how2Title: "તકો શોધો",
    how2Desc: "તમારી રુચિઓ અનુસાર કાર્યક્રમો, સ્વયંસેવી તકો અને જોડાણો બ્રાઉઝ કરો.",
    how3Title: "જોડાઓ અને સહયોગ કરો",
    how3Desc: "સમાન વિચારધારાના સામુદાયિક સભ્યો સાથે જોડાઓ.",
    how4Title: "પ્રભાવ પાડો",
    how4Desc: "તમારા યોગદાનને ટ્રેક કરો અને જુઓ કે તમે શું ફેરફાર લાવી રહ્યા છો.",
    communityTag: "સમુદાય",
    communityTitle1: "તમારું સ્થાન શોધો",
    communityTitle2: "સમુદાયમાં",
    communitySubtitle: "ભલે તમે સામુદાયિક એડમિન હો, સક્રિય સભ્ય હો, કે સ્વયંસેવક, તમારા માટે અહીં જગ્યા છે.",
    comm1Title: "સામુદાયિક સભ્યો",
    comm1Role: "સક્રિય સભ્યો અને પરિવારો",
    comm1Desc: "સમર્પિત સામુદાયિક સભ્યોના નેટવર્કમાં જોડાઓ.",
    comm1F1: "સભ્ય ડિરેક્ટરી ઍક્સેસ",
    comm1F2: "સામુદાયિક કાર્યક્રમો",
    comm1F3: "સુવિધાઓ બુક કરો",
    comm1F4: "સ્વયંસેવી તકો",
    comm1Btn: "સભ્યોને એક્સપ્લોર કરો",
    comm2Title: "સામુદાયિક એડમિન",
    comm2Role: "વ્યવસ્થાપન અને નેતૃત્વ",
    comm2Desc: "કેન્દ્રિત ડેશબોર્ડથી સામુદાયિક કામગીરીની દેખરેખ કરો.",
    comm2F1: "પૂર્ણ એડમિન ડેશબોર્ડ",
    comm2F2: "સભ્ય વ્યવસ્થાપન",
    comm2F3: "નાણાકીય અહેવાલો",
    comm2F4: "સંચાર સાધનો",
    comm2Btn: "એડમિન એક્સપ્લોર કરો",
    comm3Title: "સ્વયંસેવકો અને પ્રાયોજકો",
    comm3Role: "સમર્થન અને યોગદાન",
    comm3Desc: "સ્વયંસેવા, પ્રાયોજન અને દાન દ્વારા સામુદાયિક પહેલનું સમર્થન કરો.",
    comm3F1: "પ્રાયોજન પેકેજો",
    comm3F2: "ઇવેન્ટ પાર્ટનરશિપ",
    comm3F3: "દાન ટ્રેકિંગ",
    comm3F4: "માન્યતા અને પુરસ્કારો",
    comm3Btn: "પ્રાયોજકો એક્સપ્લોર કરો",
    ctaTitle: "ફેરફાર લાવવા તૈયાર છો?",
    ctaSubtitle: "મજબૂત સમુદાયો બનાવતા હજારો સભ્યો સાથે જોડાઓ. તમારો રસ્તો પસંદ કરો અને આજે જ શરૂ કરો.",
    ctaAdmin: "એડમિન પોર્ટલ",
    ctaAdminDesc: "તમારો સમુદાય મેનેજ કરો",
    ctaMember: "સભ્ય પોર્ટલ",
    ctaMemberDesc: "સભ્ય તરીકે જોડાઓ",
    ctaGetStarted: "હવે શરૂ કરો",
    footerRights: "© 2026 સામુદાયિક વ્યવસ્થાપન પ્લેટફોર્મ. સર્વાધિકાર સુરક્ષિત.",
    footerProduct: "ઉત્પાદન",
    footerCompany: "કંપની",
    footerLegal: "કાનૂની",
    footerAbout: "અમારા વિશે",
    footerCareers: "કારકિર્દી",
    footerContact: "સંપર્ક",
    footerPrivacy: "ગોપનીયતા નીતિ",
    footerTerms: "સેવાની શરતો",
  },
};
